import { useQuery } from '@apollo/client'
import { CaseResponse } from '../Common/Queries'
import { getLatestReopenDetail } from '../Common/ReopenUtils'
import { GET_USER_PROFILE } from '../../Layout/UserProfile/userProfle'
import { useProfile } from '../../Layout/UserProfile/UserProfileProvider'

export interface UseInternalReopenResult {
  /**
   * `true` only when the reopen user could be resolved. When `false`, the
   * internal/external nature of the reopen is unknown and callers should treat
   * it as indeterminate rather than "external".
   */
  isInternalReopenKnown: boolean
  /** `true` when the case was reopened by a Target team member (non vendor). */
  isInternalReopen: boolean
}

/**
 * Resolves whether the latest reopen on a case was performed internally
 * (by a Target team member) or externally (by a vendor).
 *
 * `reopenDetails` is selected by GET_CASE for every view, but the backend may
 * not return it (or may not allow resolving another user's profile) for a
 * vendor session, so "unknown" is kept as a distinct state from
 * "not an internal reopen".
 */
export default function useInternalReopen(
  caseData: CaseResponse | undefined | null,
): UseInternalReopenResult {
  const { userProfile } = useProfile()
  const reopenUserId =
    getLatestReopenDetail(caseData?.reopenDetails)?.reopenUser ?? ''
  // A vendor can always read their own profile, so skip the extra round trip.
  const isSelfReopen = !!reopenUserId && reopenUserId === userProfile?.userId
  const {
    data: reopenUserProfile,
    loading: reopenUserLoading,
    error: reopenUserError,
  } = useQuery(GET_USER_PROFILE, {
    variables: { userId: reopenUserId },
    skip: !reopenUserId || isSelfReopen,
    errorPolicy: 'all',
  })
  const reopenUserIsVendor = isSelfReopen
    ? userProfile?.isVendor
    : reopenUserProfile?.getUserProfile?.isVendor
  const isInternalReopenKnown =
    !!reopenUserId &&
    (isSelfReopen || (!reopenUserLoading && !reopenUserError)) &&
    typeof reopenUserIsVendor === 'boolean'

  return {
    isInternalReopenKnown,
    isInternalReopen: isInternalReopenKnown && reopenUserIsVendor === false,
  }
}
